import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import locale from '@glu/locale';
import { withTheme } from '@glu/theming';
import { Select } from '@glu/form-components';
import { Button, SMALL } from '@glu/buttons-react';
import { PlusCircleNextIcon, TrashCanIcon } from '@glu/icons-react';
import { AccessibilityText } from '@glu/utilities-react';
import { types } from './Recurrence.reducer';
import SchedulerContext from '../SchedulerContext';
import TimePicker from './TimePicker';
import useStyles from './Yearly.styles';
import { getDefaultDayInYear, getValidDayForMonth } from '../utils/recurrence';
import { getMonthOptions, getDaysOptionsForMonth } from './Yearly.options';

const Yearly = ({
  daysInYear, canSetTime, hideMinutes, multipleDays, onChange, ...rest
}) => {
  const {
    disabled, timePicker24Hour
  } = useContext(SchedulerContext);

  const classes = useStyles({
    timePicker24Hour, showingMultipleDays: daysInYear.length > 1, canSetTime, ...rest
  });

  const addDay = () => {
    onChange({
      type: types.ADD_DAY_YEAR,
      response: getDefaultDayInYear(canSetTime, timePicker24Hour),
      index: daysInYear.length
    });
  };

  const removeDay = (dayInYear) => {
    onChange({
      type: types.REMOVE_DAY_YEAR,
      response: dayInYear.id
    });
  };
  return (
    <div data-qa="yearly" className={`${classes.yearly} `}>
      <div className={classes.dates}>
        { daysInYear.map((dayInYear, index) => (
          <div key={dayInYear.id} data-qa="yearly-row">
            <div className={classes.datePicker}>
              <Select
                htmlId={`${dayInYear.id}-monthSelect`}
                name={`${dayInYear.id}-monthSelect`}
                data-qa="yearly-month-select"
                className={classes.monthSelect}
                labelText={locale.get('schedulerReact.on')}
                options={getMonthOptions(locale)}
                value={dayInYear.month.toString()}
                disabled={disabled}
                screenReaderLabel={index !== 0}
                disableInputAPI
                onChange={(e) => onChange({
                  type: types.UPDATE_DAY_YEAR,
                  response: {
                    ...dayInYear,
                    day: getValidDayForMonth(parseInt(e.target.value, 10), dayInYear.day),
                    month: parseInt(e.target.value, 10)
                  },
                  index
                })}
              />
              <Select
                htmlId={`${dayInYear.id}-daySelect`}
                name={`${dayInYear.id}-daySelect`}
                data-qa="yearly-day-select"
                className={classes.daySelect}
                labelText={locale.get('schedulerReact.on')}
                screenReaderLabel
                options={getDaysOptionsForMonth(dayInYear.month)}
                value={dayInYear.day.toString()}
                disabled={disabled}
                disableInputAPI
                onChange={(e) => onChange({
                  type: types.UPDATE_DAY_YEAR,
                  response: {
                    ...dayInYear,
                    day: parseInt(e.target.value, 10)
                  },
                  index
                })}
              />
            </div>
            {canSetTime && (
            <TimePicker
              htmlId={`${dayInYear.id}-timePicker`}
              onChange={(time) => onChange({
                type: types.UPDATE_DAY_YEAR,
                response: {
                  ...dayInYear,
                  atTime: time.value
                },
                index
              })}
              hideMinutes={hideMinutes}
              value={dayInYear.atTime}
              className={classes.timePicker}
              screenReaderLabel={index !== 0}
            />
            )}
            {multipleDays && daysInYear.length > 1 && (
            <Button
              onClick={() => removeDay(dayInYear)}
              size={SMALL}
              className={classes.removeBtn}
            >
              <AccessibilityText>{locale.get('schedulerReact.remove')}</AccessibilityText>
              <TrashCanIcon />
            </Button>
            )}
          </div>
        ))}
      </div>
      {multipleDays && (
        <Button
          onClick={addDay}
          size={SMALL}
          className={classes.addBtn}
        >
          <PlusCircleNextIcon className={classes.addBtnIcon} />
          <span className={classes.addBtnText}>{locale.get('schedulerReact.addAnother')}</span>
        </Button>
      )}
    </div>
  );
};

Yearly.propTypes = {
  /** Option to set time on day */
  canSetTime: PropTypes.bool,

  /** Hides minute controls, only displaying hours to control * */
  hideMinutes: PropTypes.bool,

  /** Array of objects containing days in year */
  daysInYear: PropTypes.instanceOf(Array),

  /** Enable multiple days to be added */
  multipleDays: PropTypes.bool,

  /** Callback to set recurrence value */
  onChange: PropTypes.func.isRequired
};

Yearly.defaultProps = {
  daysInYear: [{
    day: 1,
    month: 1
  }],
  canSetTime: true,
  multipleDays: false,
  hideMinutes: false
};

export const BareYearly = Yearly;
export default withTheme(Yearly);
